// src/rms/ads/AdSenseUnit.js
// ============================================================================
// One AdSense display unit
// ============================================================================
// Rendered only from AdUnit, only for a campaign carrying `isAdsense`, and only
// after AdUnit's viewability observer says the slot is near the viewport -- so
// the request happens for a box somebody is about to see, which is also what
// keeps the fill rate honest.
// ============================================================================

import React, { useEffect, useRef, useState } from "react";
import { loadAdSense, adSenseClient, adSenseSlotId } from "./adsense";

function AdSenseUnit({ placement, width, height }) {
  const insRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    loadAdSense()
      .then(() => {
        if (cancelled || !insRef.current) return;

        // The one guard that matters. A second push against an <ins> that has
        // already been filled throws, and worse, a push per render would be
        // manufactured impressions. AdSense stamps the element itself when it
        // takes it, so asking the DOM is more reliable than any flag we keep:
        // it survives StrictMode's double effect in development and a remount
        // that reuses the same node.
        if (insRef.current.getAttribute("data-adsbygoogle-status")) return;

        (window.adsbygoogle = window.adsbygoogle || []).push({});
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Blocked or unreachable. Render nothing rather than an empty reserved box --
  // a permanent grey rectangle reads as a broken app, and roughly a third of
  // these requests never arrive.
  if (failed) return null;

  return (
    <ins
      ref={insRef}
      className="adsbygoogle"
      style={{ display: "inline-block", width, height, maxWidth: "100%" }}
      data-ad-client={adSenseClient()}
      data-ad-slot={adSenseSlotId(placement)}
    />
  );
}

export default AdSenseUnit;
